import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded'
import { Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

/**
 * Renders a media fileId/URL that is usually an image but may be a PDF (or any non-image document).
 *
 * Report and prescription attachments are stored as a flat list of media fileIds — images and PDFs
 * share the same list, and the id alone doesn't tell us the type. So we optimistically render an
 * `<img>` and, if it fails to decode (i.e. it's a PDF/document, not an image), fall back to a
 * tappable "document" tile/panel that opens the raw file in a new tab.
 *
 * variant:
 *  - 'thumb' : small square tile for upload grids
 *  - 'panel' : large fill-the-viewer panel
 */
export default function PdfAwareImage({
  src,
  alt = 'document',
  variant = 'panel',
  imgStyle,
  onImageClick,
}) {
  const [errored, setErrored] = useState(false)

  // A new src may well be a valid image again — reset so we re-attempt rather than stick on the
  // document fallback from a previously-failed id.
  useEffect(() => {
    setErrored(false)
  }, [src])

  const openInNewTab = (e) => {
    e?.stopPropagation?.()
    if (src) window.open(src, '_blank', 'noopener,noreferrer')
  }

  if (errored) {
    if (variant === 'thumb') {
      return (
        <Box
          onClick={openInNewTab}
          title="Open document"
          sx={{
            width: '64px',
            height: '64px',
            borderRadius: '8px',
            border: '1px solid #fecaca',
            backgroundColor: '#fef2f2',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#fee2e2' },
          }}
        >
          <PictureAsPdfRoundedIcon sx={{ fontSize: 24, color: '#dc2626' }} />
          <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#dc2626', letterSpacing: '0.4px' }}>
            PDF
          </Typography>
        </Box>
      )
    }
    return (
      <Box
        onClick={openInNewTab}
        sx={{
          width: '100%',
          minHeight: { xs: 240, sm: 320 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          p: 3,
          borderRadius: '12px',
          border: '1px dashed #fca5a5',
          backgroundColor: '#fef2f2',
          cursor: 'pointer',
          textAlign: 'center',
          '&:hover': { backgroundColor: '#fee2e2' },
        }}
      >
        <PictureAsPdfRoundedIcon sx={{ fontSize: 48, color: '#dc2626' }} />
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#7f1d1d' }}>
          PDF / Document
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.75,
            borderRadius: '999px',
            backgroundColor: '#dc2626',
            color: 'white',
            fontSize: '12px',
            fontWeight: 700,
          }}
        >
          <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
          Open document
        </Box>
      </Box>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      onClick={onImageClick}
      onError={() => setErrored(true)}
      style={imgStyle}
    />
  )
}
